import { useEffect, useRef } from 'react'
import { useToast } from '../contexts/ToastContext'
import { APP_VERSION } from '../version'

type VersionManifest = {
  version?: string
}

const VERSION_POLL_INTERVAL_MS = 5 * 60 * 1000

async function fetchLatestVersion(signal: AbortSignal) {
  const response = await fetch(`/version.json?t=${Date.now()}`, {
    cache: 'no-store',
    signal,
  })

  if (!response.ok) {
    throw new Error(`Version request failed with status ${response.status}`)
  }

  const data = (await response.json()) as VersionManifest
  return data.version?.trim() ?? ''
}

export default function AppVersionNotifier() {
  const { addToast } = useToast()
  const notifiedRef = useRef(false)
  const inFlightRef = useRef(false)

  useEffect(() => {
    if (!APP_VERSION || APP_VERSION === '__APP_VERSION__') {
      return undefined
    }

    let mounted = true

    const checkVersion = async () => {
      if (inFlightRef.current) {
        return
      }

      inFlightRef.current = true
      const controller = new AbortController()

      try {
        const latestVersion = await fetchLatestVersion(controller.signal)
        if (!mounted || notifiedRef.current || !latestVersion || latestVersion === APP_VERSION) {
          return
        }

        notifiedRef.current = true
        addToast('New version of DashboardInsights is available.', 'info', 0, {
          label: 'Reload now',
          onClick: () => window.location.reload(),
        })
      } catch {
        // Ignore background version check failures.
      } finally {
        inFlightRef.current = false
      }
    }

    void checkVersion()

    const intervalId = window.setInterval(() => {
      void checkVersion()
    }, VERSION_POLL_INTERVAL_MS)

    return () => {
      mounted = false
      window.clearInterval(intervalId)
    }
  }, [addToast])

  return null
}
